import axios from "axios";
import alertService from "../store/alertService";
import store from "../state/store";
import { ActionType } from "../state/action-types/type";
import AuthService from "./authService";
import authStorage from "./authStorage";
import useAccessBlockStore from "../store/useAccessBlockStore";

// Backend CustomError codes that map to a whole-tenant access block (see the customer backend's
// CustomError enum). They ride on the JSON ExceptionDTO body as `errorCode`.
const ERROR_CODE_COMPANY_OFFBOARDED = 39;
const ERROR_CODE_SUBSCRIPTION_EXPIRED = 40;

let responseInterceptorId: number | null = null;

// A single in-flight refresh shared by every request that 401s at the same
// time, so we only hit /api/auth/refresh once and the rotated cookie isn't wasted.
let refreshPromise: Promise<string> | null = null;

/**
 * Exchange the HttpOnly refresh-token cookie for a fresh access token (the
 * browser sends the cookie automatically), persisting the new access token /
 * current roles in Redux + storage. Resolves with the new access token; rejects
 * if the cookie is missing/expired/revoked.
 */
const refreshAccessToken = async (): Promise<string> => {
  const response = await AuthService.refresh();
  store.dispatch({
    type: ActionType.REFRESH_TOKEN_SUCCESS,
    payload: { user: response.data },
  });
  return response.data.token;
};

/**
 * A blob-typed request (the Excel exports, the deposit PDF) receives its *error* body as a Blob too,
 * not as parsed JSON. That hides `errorCode` from the whole-tenant checks below (so a 402 would never
 * reach the renewal screen) and leaves the alert with nothing to read but `statusText`. Parse the body
 * back into a plain object once, here, so every consumer behaves exactly as it does for a JSON request.
 *
 * Best-effort: a body that is not JSON (an actual xlsx, an HTML error page) is left untouched, and the
 * caller's `globalErrorFallbackMessage` still applies.
 */
const unwrapBlobErrorBody = async (error: any): Promise<void> => {
  const data = error?.response?.data;
  if (typeof Blob === "undefined" || !(data instanceof Blob)) return;

  try {
    const text =
      typeof data.text === "function"
        ? await data.text()
        : await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result ?? ""));
            reader.onerror = () => reject(reader.error);
            reader.readAsText(data);
          });

    const trimmed = text.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      error.response.data = JSON.parse(trimmed);
    }
  } catch {
    // Leave the blob in place: the fallback-message path still surfaces something useful.
  }
};

export const setupHttpErrorInterceptor = () => {
  if (responseInterceptorId !== null) return;

  responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;
      const url: string = originalRequest?.url ?? "";

      // Must run before errorCode is read: on a blob-typed request the body is a Blob, so the
      // whole-tenant codes below would be invisible and a 402 would never reach the renewal screen.
      // The Blob check is deliberately made HERE, synchronously, rather than inside the helper: an
      // unconditional await would push every ordinary JSON error one microtask later, which is enough
      // to break the single-flight refresh dedupe below.
      if (typeof Blob !== "undefined" && error?.response?.data instanceof Blob) {
        await unwrapBlobErrorBody(error);
      }
      const errorCode = error?.response?.data?.errorCode;

      // Whole-tenant access blocks the backend signals on any request. Handle them first and
      // short-circuit (no generic red alert: the dedicated screen is the UX). App's AccessBlockGuard
      // reads the store and routes to the right screen.
      // - Off-boarded (403 / errorCode 39): company deactivated. Also tear down the now-useless session
      //   so the app doesn't keep retrying with a token the backend will always reject.
      // - Subscription expired (402 / errorCode 40): keep the session; the user stays signed in and is
      //   funnelled to the renewal screen (payment/subscription lanes stay reachable).
      if (status === 403 && errorCode === ERROR_CODE_COMPANY_OFFBOARDED) {
        useAccessBlockStore.getState().setBlock("COMPANY_OFFBOARDED");
        AuthService.logout();
        store.dispatch({ type: ActionType.LOGOUT });
        return Promise.reject(error);
      }
      if (status === 402 && errorCode === ERROR_CODE_SUBSCRIPTION_EXPIRED) {
        useAccessBlockStore.getState().setBlock("SUBSCRIPTION_EXPIRED");
        return Promise.reject(error);
      }

      // Never try to refresh for the auth endpoints themselves: a 401 from
      // /auth/login, /auth/refresh or /auth/logout is terminal and must surface
      // to the caller.
      const isAuthEndpoint =
        url.includes("auth/login") ||
        url.includes("auth/refresh") ||
        url.includes("auth/logout");

      // On a 401 from a protected endpoint, attempt a one-time silent refresh
      // (via the refresh cookie) and replay the original request. We gate on an
      // existing access token because the refresh cookie is HttpOnly (JS can't
      // see it) so a stored (now-expired) access token is our "logged in" signal.
      if (
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthEndpoint &&
        authStorage.getAccessToken()
      ) {
        originalRequest._retry = true;
        try {
          if (!refreshPromise) {
            refreshPromise = refreshAccessToken().finally(() => {
              refreshPromise = null;
            });
          }
          const newToken = await refreshPromise;
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: "Bearer " + newToken,
          };
          return axios(originalRequest);
        } catch (refreshError) {
          // Refresh failed → the session is dead. Clear the stored tokens so
          // the app falls back to login; let the original 401 propagate to the
          // existing per-screen handling (which redirects to /login).
          AuthService.logout();
          store.dispatch({ type: ActionType.LOGOUT });
        }
      }

      if (!originalRequest?.skipGlobalErrorAlert) {
        alertService.showApiError(error, originalRequest?.globalErrorFallbackMessage);
      }

      return Promise.reject(error);
    }
  );
};
